import React from 'react';
import { Table, Typography } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { createPreflightChecksColumns } from '../utils/tableUtils';
import { getFilteredChecks } from '../utils/preflightUtils';
import { RecoveryUI } from './RecoveryUI';
import './PreflightChecksTable.scss';

const { Text } = Typography;

/**
 * Reusable PreflightChecksTable component for displaying detailed preflight checks
 * @param {Array} checks - Array of preflight checks
 * @param {string} componentId - ID of the parent preflight component
 * @param {boolean} inspectSourcesMode - Whether in source inspection mode
 * @param {Map} nonErrorsHiddenPerComponent - Map of component visibility states
 * @param {Function} onRecoveryChange - Callback for recovery state changes
 * @param {Function} getRecoveryState - Function to get recovery state
 */
export const PreflightChecksTable = ({
    checks,
    componentId,
    inspectSourcesMode,
    nonErrorsHiddenPerComponent,
    onRecoveryChange,
    getRecoveryState,
}) => {
    const filteredChecks = getFilteredChecks(checks, componentId, nonErrorsHiddenPerComponent, inspectSourcesMode);

    const columns = createPreflightChecksColumns({
        onRecoveryChange,
        componentId,
    });

    // Enhanced columns with recovery UI
    const enhancedColumns = columns.map((column) => {
        if (column.key === 'description') {
            return {
                ...column,
                render: (text, record) => (
                    <div>
                        {text}
                        {/* Show errors if any */}
                        {record.state === 'error' && record.errors && record.errors.length > 0 && (
                            <div className="error-container">
                                <div className={`error-header ${record.error_type === 'warning' ? 'warning' : ''}`}>
                                    {record.error_type === 'warning' ? 'Warning(s):' : 'Error(s):'}
                                </div>
                                {record.errors.map((error, index) => (
                                    <div key={index} className="error-item">
                                        <div className="error-content">
                                            <ExclamationCircleFilled
                                                className={`error-icon ${record.error_type === 'warning' ? 'warning' : 'error'}`}
                                            />
                                            <div className="error-details">
                                                <div className="error-message">{error.message}</div>
                                                {error.resolution && (
                                                    <div className="error-resolution">
                                                        <span className="resolution-label">Resolution: </span>
                                                        <span className="resolution-text">{error.resolution}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {/* Render recovery UI if this error has recovery options */}
                                        {error.recovery && onRecoveryChange && getRecoveryState && (
                                            <div className="recovery-container">
                                                <RecoveryUI
                                                    componentId={componentId}
                                                    checkId={record.id}
                                                    error={error}
                                                    errorIndex={index}
                                                    onRecoveryChange={onRecoveryChange}
                                                    getRecoveryState={getRecoveryState}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ),
            };
        }
        return column;
    });

    return (
        <div className="preflight-checks-table">
            <Table size="small" pagination={false} dataSource={filteredChecks} columns={enhancedColumns} rowKey="id" />
        </div>
    );
};
